import { Alert } from '@/components/alert';
import { Form, Input } from '@/components/form';
import { FormattedMessage, useIntl } from '@/components/formatted_message/FormattedMessage';
import { Flex } from '@/components/layouting';
import type { FC } from 'react';
import { RecoveryCode as messages } from '../messages';
import { LostDeviceStep } from '../enums/lost-device-step';
import { ButtonAnt } from '@/components/buttons/button/ButtonAnt';
import { useAuthContext } from '@/contexts/AuthContext';
import { AuthContentLayout } from '@/routes/auth/components/AuthContentLayout';

const RECOVERY_CODE_FORM_ID = 'recovery-code-form';

interface RecoveryCodeContentProps {
  onStepChange: (step: LostDeviceStep) => void;
}

interface RecoveryCodeFormValues {
  recoveryCode: string;
}

export const RecoveryCodeContent: FC<RecoveryCodeContentProps> = ({ onStepChange }) => {
  const intl = useIntl();
  const [form] = Form.useForm<RecoveryCodeFormValues>();
  const {
    verifyBackupCodeAsync,
    error: { verifyBackupCode: verifyBackupCodeError },
    loading: { isLoadingVerifyBackupCode },
  } = useAuthContext();

  const handleFinish = async ({ recoveryCode }: RecoveryCodeFormValues) => {
    try {
      await verifyBackupCodeAsync({ code: recoveryCode });
    } catch (e: unknown) {
      console.error('Error verifying backup code:', e);
    }
  };

  return (
    <AuthContentLayout
      alert={
        <>
          {Boolean(verifyBackupCodeError) && (
            <Alert
              showIcon
              type="error"
              description={<FormattedMessage {...messages.errorMessage} />}
              data-cy="verify-backup-code-error-alert"
            />
          )}
          <Alert showIcon type="info" description={<FormattedMessage {...messages.alertMessage} />} />
        </>
      }
      actions={
        <Flex gap={8} vertical>
          <ButtonAnt
            block
            form={RECOVERY_CODE_FORM_ID}
            htmlType="submit"
            loading={isLoadingVerifyBackupCode}
            size="large"
            type="primary"
          >
            <FormattedMessage {...messages.verifyCode} />
          </ButtonAnt>
          <ButtonAnt block onClick={() => onStepChange(LostDeviceStep.RecoveryMethod)} size="large">
            <FormattedMessage {...messages.cancel} />
          </ButtonAnt>
        </Flex>
      }
    >
      <Form id={RECOVERY_CODE_FORM_ID} layout="vertical" form={form} onFinish={handleFinish}>
        <Form.Item label={intl.formatMessage(messages.recoveryCode)} name="recoveryCode">
          <Input autoFocus placeholder="XXXX-XXXX" />
        </Form.Item>
      </Form>
    </AuthContentLayout>
  );
};
